/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import useT, { UseTranslationOptions } from "../../../Basis/Internationalisierung/Hooks/useTranslation";
import { FilterOperator } from "../Commands/Filter";
import { ZusatzfeldDefinitionUrsprung } from "../Queries/ZusatzfeldDefinition";
import { ZusatzfeldTypen } from "../Queries/ZusatzfeldType";
import i18n from "./../../../Basis/Internationalisierung/i18n";
import Artikelstatus from "./../Commands/Artikelstatus";

export const fixedT = i18n.getFixedT(null, "i18nDomainDrivenDesign");
export const useTranslation = (options?: UseTranslationOptions) => useT("i18nDomainDrivenDesign", options);

export const artikelstatusTranslationKeys: { [key in Artikelstatus]: `artikelstatus.${key}` } = {
    ENTWURF: "artikelstatus.ENTWURF",
    OEFFENTLICH: "artikelstatus.OEFFENTLICH",
    INTERN: "artikelstatus.INTERN",
}

export const zusatzfeldTypeTranslationKeyMap = {
    [ZusatzfeldTypen.auswahl]: "zusatzfeldTypen.auswahl",
    [ZusatzfeldTypen.checkbox]: "zusatzfeldTypen.checkbox",
    [ZusatzfeldTypen.datumzeit]: "zusatzfeldTypen.datumzeit",
    [ZusatzfeldTypen.dezimalzahl]: "zusatzfeldTypen.dezimalzahl",
    [ZusatzfeldTypen.ganzzahl]: "zusatzfeldTypen.ganzzahl",
    [ZusatzfeldTypen.prozent]: "zusatzfeldTypen.prozent",
    [ZusatzfeldTypen.richtext]: "zusatzfeldTypen.richtext",
    [ZusatzfeldTypen.text]: "zusatzfeldTypen.text",
    [ZusatzfeldTypen.mehrfachauswahl]: "zusatzfeldTypen.mehrfachauswahl",
}

type ZusatzfeldDefinitionUrsprungKeys = keyof typeof ZusatzfeldDefinitionUrsprung;

function istInternTranslations(base: string) {
    const trans: { [key in ZusatzfeldDefinitionUrsprungKeys]: string } = {
        [ZusatzfeldDefinitionUrsprung.Adresse]: `${base}.adresse`,
        [ZusatzfeldDefinitionUrsprung.Seminar]: `${base}.seminar`,
        [ZusatzfeldDefinitionUrsprung.Ressourcenkategorie]: `${base}.ressourceKategorie`,
        [ZusatzfeldDefinitionUrsprung.Teilnahme]: `${base}.teilnahme`,
        [ZusatzfeldDefinitionUrsprung.Seminarreihe]: `${base}.seminarreihe`,
        [ZusatzfeldDefinitionUrsprung.Dozierend]: `${base}.dozierend`
    };

    return trans;
}

type FilterOperatorKeys = { [K in FilterOperator]: `filterTable.${Lowercase<K>}` };
const filterOperatorKeys: FilterOperatorKeys = {
    "ENTHAELT": "filterTable.enthaelt",
    "GLEICH": "filterTable.gleich",
    "UNGLEICH": "filterTable.ungleich",
    "KLEINER": "filterTable.kleiner",
    "KLEINERGLEICH": "filterTable.kleinergleich",
    "GROESSER": "filterTable.groesser",
    "GROESSERGLEICH": "filterTable.groessergleich",
    "HATKEINENWERT": "filterTable.hatkeinenwert",
    "ENTHAELTNICHT": "filterTable.enthaeltnicht",
};

export const translationKeys = {
    cloudTypen: {
        dokumentstapel: "cloudTypen.dokumentstapel",
        dokumentdaten: "cloudTypen.dokumentdaten",
        kategorie: "cloudTypen.kategorie",
        veranstaltung: "cloudTypen.veranstaltung",
        veranstaltungsreihe: "cloudTypen.veranstaltungsreihe",
        veranstaltungstexte: "cloudTypen.veranstaltungstexte",
        veranstaltungsTeamsEinstellungen: "cloudTypen.veranstaltungsTeamsEinstellungen",
        veranstaltungsZoomEinstellungen: "cloudTypen.veranstaltungsZoomEinstellungen",
        veranstaltungsEvaluation: "cloudTypen.veranstaltungsEvaluation",
        teilnahmen: "cloudTypen.teilnahmen",
        teilnahmenadresse: "cloudTypen.teilnahmenadresse",
        beleg: "cloudTypen.beleg",
        belegposition: "cloudTypen.belegposition",
        teilnahmenleistung: "cloudTypen.teilnahmenleistung",
        teilnahme: "cloudTypen.teilnahme",
        fibuExport: "cloudTypen.fibuExport",
        bestellung: "cloudTypen.bestellung",
        bestellposition: "cloudTypen.bestellposition",
        bestellterArtikel: "cloudTypen.bestellterArtikel",
        teilnahmeZertifikat: "cloudTypen.teilnahmeZertifikat",
        veranstaltungsDozierendZertifikat: "cloudTypen.veranstaltungsDozierendZertifikat",
        anfechtung: "cloudTypen.anfechtung",
        ressource: "cloudTypen.ressource",
        ressourceKategorie: "cloudTypen.ressourceKategorie",
        belegung: "cloudTypen.belegung",
        eMail: "cloudTypen.eMail",
        csvExport: "cloudTypen.csvExport",
        mahlzeit: "cloudTypen.mahlzeit",
        regel: "cloudTypen.regel",
        csvImport: "cloudTypen.csvImport",
        adresse: "cloudTypen.adresse"
    },
    ladeTypenUtil: {
        typenNichtGeladen: "ladeTypenUtil.typenNichtGeladen",
        typNichtGeladen: "ladeTypenUtil.typNichtGeladen",
        feldNichtGeladen: "ladeTypenUtil.feldNichtGeladen",
    },
    artikelstatus: artikelstatusTranslationKeys,
    zusatzfelderView: {
        bezeichnung: "zusatzfelderView.bezeichnung",
        type: "zusatzfelderView.type",
        anlegen: "zusatzfelderView.anlegen",
        loeschen: "zusatzfelderView.loeschen",
        zusatzfelderZuAdressen: "zusatzfelderView.zusatzfelderZuAdressen",
        zusatzfelderZuTeilnahmen: "zusatzfelderView.zusatzfelderZuTeilnahmen",
        intern: "zusatzfelderView.intern",
        internTooltip: istInternTranslations("zusatzfelderView.internTooltip"),
        alleAuswaehlenTitel: "zusatzfelderView.alleAuswaehlenTitel",
        alleAbwaehlenTitel: "zusatzfelderView.alleAbwaehlenTitel"
    },
    loescheZusatzfeldDefinitionenDialog: {
        dialogText: "loescheZusatzfeldDefinitionenDialog.dialogText",
        dialogCaption: "loescheZusatzfeldDefinitionenDialog.dialogCaption",
        submitButtonCaption: "loescheZusatzfeldDefinitionenDialog.submitButtonCaption",
        abortButtonCaption: "loescheZusatzfeldDefinitionenDialog.abortButtonCaption",
    },
    zusatzfeldDefinitionView: {
        zusatzfeldDefinition: "zusatzfeldDefinitionView.zusatzfeldDefinition",
        details: "zusatzfeldDefinitionView.details",
        auswahlmoeglichkeiten: "zusatzfeldDefinitionView.auswahlmoeglichkeiten",
    },
    zusatzfeldDefinitionDetailsView: {
        bezeichnung: "zusatzfeldDefinitionDetailsView.bezeichnung",
        name: "zusatzfeldDefinitionDetailsView.name",
        type: "zusatzfeldDefinitionDetailsView.type",
        details: "zusatzfeldDefinitionDetailsView.details",
        speichern: "zusatzfeldDefinitionDetailsView.speichern",
        istIntern: istInternTranslations("zusatzfeldDefinitionDetailsView.istIntern"),
        istInternTooltip: istInternTranslations("zusatzfeldDefinitionDetailsView.istInternTooltip"),
    },
    zusatzfeldDefinitionAuswahlmoeglichkeitenView: {
        bearbeiten: "zusatzfeldDefinitionAuswahlmoeglichkeitenView.bearbeiten",
        hinzufuegen: "zusatzfeldDefinitionAuswahlmoeglichkeitenView.hinzufuegen",
        loeschen: "zusatzfeldDefinitionAuswahlmoeglichkeitenView.loeschen",
        auswahlmoeglichkeit: "zusatzfeldDefinitionAuswahlmoeglichkeitenView.auswahlmoeglichkeit",
    },
    fuegeAuswahlmoeglichkeitZuZusatzfeldDefinitionHinzuDialog: {
        dialogCaption: "fuegeAuswahlmoeglichkeitZuZusatzfeldDefinitionHinzuDialog.dialogCaption",
        dialogText: "fuegeAuswahlmoeglichkeitZuZusatzfeldDefinitionHinzuDialog.dialogText",
        submitButtonCaption: "fuegeAuswahlmoeglichkeitZuZusatzfeldDefinitionHinzuDialog.submitButtonCaption",
        abortButtonCaption: "fuegeAuswahlmoeglichkeitZuZusatzfeldDefinitionHinzuDialog.abortButtonCaption",
        bezeichnung: "fuegeAuswahlmoeglichkeitZuZusatzfeldDefinitionHinzuDialog.bezeichnung",
    },
    aendereAuswahlmoeglichkeitZuZusatzfeldDefinitionDialog: {
        dialogCaption: "aendereAuswahlmoeglichkeitZuZusatzfeldDefinitionDialog.dialogCaption",
        dialogText: "aendereAuswahlmoeglichkeitZuZusatzfeldDefinitionDialog.dialogText",
        submitButtonCaption: "aendereAuswahlmoeglichkeitZuZusatzfeldDefinitionDialog.submitButtonCaption",
        abortButtonCaption: "aendereAuswahlmoeglichkeitZuZusatzfeldDefinitionDialog.abortButtonCaption",
        bezeichnung: "aendereAuswahlmoeglichkeitZuZusatzfeldDefinitionDialog.bezeichnung",
    },
    zusatzfeldDefinitionAuswahlmoeglichkeitenBreadcrumb: {
        auswahlmoeglichkeiten: "zusatzfeldDefinitionAuswahlmoeglichkeitenBreadcrumb.auswahlmoeglichkeiten",
    },
    legeZusatzfeldDefinitionAnDialog: {
        bezeichnung: "legeZusatzfeldDefinitionAnDialog.bezeichnung",
        type: "legeZusatzfeldDefinitionAnDialog.type",
        submitButtonCaption: "legeZusatzfeldDefinitionAnDialog.submitButtonCaption",
        abortButtonCaption: "legeZusatzfeldDefinitionAnDialog.abortButtonCaption",
        dialogCaption: "legeZusatzfeldDefinitionAnDialog.dialogCaption",
        dialogText: "legeZusatzfeldDefinitionAnDialog.dialogText",
        nurInternVerwenden: istInternTranslations("legeZusatzfeldDefinitionAnDialog.nurInternVerwenden"),
        nurInternVerwendenTooltip: istInternTranslations("legeZusatzfeldDefinitionAnDialog.nurInternVerwendenTooltip"),
    },
    filterTable: {
        ja: "filterTable.ja",
        nein: "filterTable.nein",
        spalte: "filterTable.spalte",
        operator: "filterTable.operator",
        wert: "filterTable.wert",
        filterHinzufuegen: "filterTable.filterHinzufuegen",
        spaltenLabel: "filterTable.spaltenLabel",
        spaltenTitel: "filterTable.spaltenTitel",
        operatorLabel: "filterTable.operatorLabel",
        operatorTitel: "filterTable.operatorTitel",
        filterwertTitel: "filterTable.filterwertTitel",
        filterwertLabel: "filterTable.filterwertLabel",
        loeschenButtonTitel: "filterTable.loeschenButtonTitel",
        loeschenButtonLabel: "filterTable.loeschenButtonLabel",
        ...filterOperatorKeys
    },
};